import { AUTOCOMPLETE_MIN_CHARS, Typeahead, TypeaheadOption } from '@msaf/core-react'
import debounce from 'lodash.debounce'
import { useEffect, useState } from 'react'
import { InputAction, Options } from 'react-select'
import useAdvancedSearch from '../../../../../../hooks/use-advanced-search'
import { DEBOUNCE_DELAY } from '../../../../../../services/constants'
import {
  createDefaultFilterSet,
  createFilterSet,
  FilterInstance,
  getSearchResult,
  SearchQuery,
  SearchResultRow,
} from '../../../../generic-search-common/src'
import { getSearchTemplateWithDisplayFilters } from '../../hooks/search'

export interface QuickSearchProps {
  maxNumberOfResults?: number // Maximum number of results returned from quick search
  placeholderText?: string // Placeholder string inside search box to tell user what fields can be searched
  searchTypeKey: string // The type of search being performed
  onRowClick: (id: string) => void // Redirects user to specific search item details
  getDisplayOptions: (suggestions: Array<SearchResultRow>) => TypeaheadOption[]
}

export function QuickSearch({
  maxNumberOfResults = 10,
  searchTypeKey,
  placeholderText = '',
  onRowClick,
  getDisplayOptions,
}: QuickSearchProps) {
  const [selectedOption, setSelectedOption] = useState<TypeaheadOption | null>(null)
  const [searchQuery, setSearchQuery] = useState<SearchQuery>()
  const { searchTemplate, requestOptions } = useAdvancedSearch({ searchTypeKey })

  useEffect(() => {
    if (searchTemplate) {
      // Filter out the hidden filters so that the rest of the logic need not worry about it
      const template = getSearchTemplateWithDisplayFilters(searchTemplate)
      const currentViewKey = template.views[0].viewKey
      const defaultFilters = createDefaultFilterSet(template.filters)
      const defaultFilterSet = createFilterSet(defaultFilters, 'AND')
      const searchQuery: SearchQuery = {
        viewKey: currentViewKey,
        searchTypeKey: template.searchTypeKey,
        requestedPage: 1,
        requestedPageLength: maxNumberOfResults,
        filterSet: defaultFilterSet.filterSet,
      }
      setSearchQuery(searchQuery)
    }
  }, [searchTemplate, maxNumberOfResults])

  const getSearchFilters = (filterValue: string): Array<FilterInstance> => {
    return [
      {
        filterKey: 'quickSearch',
        filterValue: filterValue,
        filterOperator: '@@',
      },
    ]
  }

  const fetchSuggestions = async (inputValue: string, callback: (options: TypeaheadOption[]) => void) => {
    const filters = getSearchFilters(inputValue)
    const { filterSet } = createFilterSet([createFilterSet(filters, 'OR')], 'AND')
    const appliedSearchQuery: SearchQuery = {
      ...searchQuery,
      filterSet,
    }
    const response = await getSearchResult(appliedSearchQuery, {
      ...requestOptions,
      urlPrefix: `${requestOptions.urlPrefix}/generic-search`,
    })
    const suggestions: Array<SearchResultRow> = response?.searchResult?.rows
    if (suggestions) {
      const displayOptions: TypeaheadOption[] = getDisplayOptions(suggestions)
      callback(displayOptions)
    }
  }

  const loadOptions = (inputValue: string, callback: (options: Options<TypeaheadOption>) => void) => {
    if (inputValue == null || inputValue === '' || inputValue.length < AUTOCOMPLETE_MIN_CHARS) return callback([])
    if (inputValue.length >= AUTOCOMPLETE_MIN_CHARS) {
      fetchSuggestions(inputValue, callback)
    }
  }

  const handleChange = (option: TypeaheadOption, _triggeredAction?: InputAction) => {
    setSelectedOption(option && option.value ? option : { label: '', value: '' })
    option.value && onRowClick(option.value)
  }

  return (
    <Typeahead
      selectType='async'
      labelledBy={'quickSearch'}
      options={[]}
      loadOptions={debounce(loadOptions, DEBOUNCE_DELAY)}
      handleChange={handleChange}
      selectedOption={selectedOption}
      placeholder={placeholderText}
      autoFocus
    />
  )
}
